import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../Spinner';
import {forgetLinkToken, LINK_NOT_AVAILABLE, NETWORK_ERROR, publicLinkPath, readLinkToken} from './connectionLinksApi';
import {PublicConnectFrame, PublicStatusIcon} from './PublicConnectFrame';
import {usePublicPageHead} from './usePublicPageHead';

const RAW = {interpolation: {escapeValue: false}};

export const CANCELLED = 'CANCELLED';

// `retry`: el link sigue sirviendo y volver a abrirlo puede andar. `terminal`: no hay nada que
// reintentar con ese link, así que se olvida el token. Lo que no está acá (un `ERROR` genérico, un
// código nuevo del backend) cae en `generic_error` con reintento: un link que ya no sirve lleva, a
// lo sumo, a la página que dice «ya se usó o venció».
const VIEW_BY_STATUS = {
    [CANCELLED]: {kind: 'soft', key: 'cancelled', retry: true},
    MERCADO_ADS_NOT_MAIN_USER: {kind: 'error', key: 'not_main_user', retry: true},
    MERCADO_ADS_NO_PRODUCT_ADS: {kind: 'error', key: 'no_product_ads', retry: true},
    MERCADO_ADS_TEMPORARY_ERROR: {kind: 'error', key: 'temporary', retry: true},
    MERCADO_ADS_STATE_INVALID: {kind: 'error', key: 'state_invalid', retry: true},
    [NETWORK_ERROR]: {kind: 'error', key: 'network_error', retry: true},
    // Neutro a propósito: el vendedor no se entera de si su cuenta ya la usa la agencia u otro cliente.
    CONNECTION_NOT_AVAILABLE: {kind: 'error', key: 'not_available', terminal: true},
    CONNECTOR_NOT_CONFIGURED: {kind: 'error', key: 'not_available', terminal: true},
    [LINK_NOT_AVAILABLE]: {kind: 'soft', key: 'expired', terminal: true},
};

const GENERIC_ERROR_VIEW = {kind: 'error', key: 'generic_error', retry: true};

/** Qué mostrar para `result` (la respuesta del callback público, `{status: 'CANCELLED'}` o `null`). */
export function resultView(result) {
    if (!result) return {kind: 'loading'};
    if (result.status === 'OK') return {kind: 'done', terminal: true};
    return VIEW_BY_STATUS[result.status] || GENERIC_ERROR_VIEW;
}

/**
 * La vuelta de Mercado Libre en el flujo del link (`state` `lk.`). La monta el callback de `ma-frontend`
 * con la respuesta de `postPublicLinkCallback`, y con `result={null}` mientras espera: así el vendedor
 * ve el mismo marco de la página del link desde el primer frame.
 *
 * «Volver a intentar» vuelve a `/conectar/<token>` con el token que la página guardó en
 * `sessionStorage` al salir; si no está (otra pestaña, el navegador lo bloquea), el texto va sin botón.
 */
export function PublicConnectResult({result}) {
    const {t} = useTranslation();
    const [token] = useState(readLinkToken);
    const view = resultView(result);

    usePublicPageHead(t('mercado_ads_link.public.page_title'));

    useEffect(() => {
        if (view.terminal) forgetLinkToken();
    }, [view.terminal]);

    if (view.kind === 'loading') {
        return (
            <PublicConnectFrame>
                <div className="pcl-loader" role="status">
                    <Spinner color="positive"/>
                    <p className="pcl-text">{t('mercado_ads_link.public.connecting')}</p>
                </div>
            </PublicConnectFrame>
        );
    }

    if (view.kind === 'done') {
        const agency = (result.data && result.data.agency_name) || t('mercado_ads_link.public.your_agency');
        return (
            <PublicConnectFrame>
                <div className="pcl-result">
                    <PublicStatusIcon kind="done"/>
                    <h1 className="pcl-title">{t('mercado_ads_link.public.done_title')}</h1>
                    <p className="pcl-text">{t('mercado_ads_link.public.done_text', {agency, ...RAW})}</p>
                </div>
            </PublicConnectFrame>
        );
    }

    return (
        <PublicConnectFrame>
            <div className="pcl-result" role="alert">
                <PublicStatusIcon kind={view.kind}/>
                {view.kind === 'error' && (
                    <h1 className="pcl-title">{t('mercado_ads_link.public.error_title')}</h1>
                )}
                {/* Sin título propio, el aviso suave (cancelado, vencido) ES el título, como en la página del link. */}
                <p className={view.kind === 'soft' ? 'pcl-title' : 'pcl-text'}>
                    {t(`mercado_ads_link.public.${view.key}`)}
                </p>
                {view.retry && token && (
                    <a className="pcl-cta -secondary" href={publicLinkPath(token)}>
                        {t('mercado_ads_link.public.retry')}
                    </a>
                )}
            </div>
        </PublicConnectFrame>
    );
}

export default PublicConnectResult;
